import React, {
  useState,
  useContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { ThemeContext } from 'styled-components';
import { API, graphqlOperation } from 'aws-amplify';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../../../graphql/iot-documents';
import {
  Button,
  Loader,
  Carousel,
  OperationalStatus,
} from '../../../../components';
import { images, icons } from '../../../../assets';
import {
  formatValue,
  formatEnergyAutoUnit,
  notifyError,
  createReconnectingSubscription,
  sessionCache,
} from '../../../../utils';
import {
  getRendimentoHidraulico,
  getThingOperationalStatus,
} from '../../../../services';
import {
  BLUEBOX_HEADLINE_CARDS,
  formatBlueboxValue,
  isBlueboxThings,
} from '../../../../constants/bluebox';
import { applySimulator, simulatedTilt } from '../../../../utils/simulator';
import { useSimulator } from '../../../../contexts/simulator';
import BlueboxStatus from './bluebox-status';
import * as s from './styled-config';

const ConfigAplicacao = ({
  things,
  setSelectedThing,
  selectedThing,
  aplicacao,
  type,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  // Seletor especifico: state.aplicacao tem varios campos atualizando
  // por subscription (realtimeData, velocimetro). Pegar so o que
  // precisamos evita re-render desnecessario.
  const operationalStatus = useSelector(
    (state) => state.aplicacao.operationalStatus
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const [rawThingsData, setThingsData] = useState([]);
  const sharedIotData = useSelector((state) => state.aplicacao.velocimetro);
  // Modo simulador (so dev): quando ligado, preenche os dados das things
  // com valores fake pra visualizar o layout mesmo sem telemetria real.
  // Desligado, `thingsData` === dado real (comportamento normal).
  const { enabled: simulatorEnabled, cycle: simulatorCycle } = useSimulator();
  const isBluebox = isBlueboxThings(things);
  const sourceThingsData = useMemo(
    () =>
      isBluebox
        ? things.map(({ id }) => sharedIotData[id]).filter(Boolean)
        : rawThingsData,
    [isBluebox, things, sharedIotData, rawThingsData]
  );
  const thingsData = useMemo(
    () => applySimulator(sourceThingsData, things, simulatorEnabled),
    [sourceThingsData, things, simulatorEnabled]
  );
  const [zoomCard] = useState(true);
  const [rendimentoHidraulico, setRendimentoHidraulico] = useState('');
  const [loading] = useState(false);
  const [unidadeMedida, setUnidadeMedida] = useState('litros');
  const { colors, title: themeTitle } = useContext(ThemeContext);

  // BLUEBOX e um THING TYPE proprio (id 3), sempre dentro de uma appliance
  // EBA — por isso detectamos pelo tipo da THING (nao da appliance, que
  // seria 'eba'/'bomba'). Antes caia no fluxo generico de bomba e mostrava
  // campos que a bluebox nao publica (tela vazia). A bluebox tem suas
  // proprias grandezas (ver constants/bluebox + BlueboxDetail).
  // Imagem do diagrama varia por tipo (ucha/eba/bluebox) E por tema
  // (light/dark) — o design/cores da arte mudam conforme o fundo. Mapa
  // explicito pra ficar obvio qual arquivo trocar. Fallback pra dark se
  // o tema vier inesperado.
  const machineImageByTheme = isBluebox
    ? { light: images.Bluebox3dLight, dark: images.Bluebox3dDark }
    : type === 'ucha'
    ? { light: images.TGA3dLight, dark: images.TGA3dDark }
    : { light: images.Bomba3dLight, dark: images.Bomba3dDark };
  const machineImage =
    machineImageByTheme[themeTitle === 'light' ? 'light' : 'dark'];

  const afterChange = (direction) => {
    let index = activeIndex;

    if (direction === 'left') {
      if (activeIndex !== 0) index -= 1;
    } else if (activeIndex !== things.length - 1) {
      index += 1;
    }

    setActiveIndex(index);
    setSelectedThing(things[index]);
  };

  const isBomba = things[0].type.type === 'bomba';

  // Mesma estrategia da InformacoesAplicacao: acumulador unico, mountedRef
  // gating, array subscriptions[] que cleanup percorre para unsubscribe TODAS.
  // Antes o forEach async criava subscriptions sem armazenar a referencia em
  // lugar nenhum — navegar entre maquinas deixava subscriptions vivas que
  // continuavam disparando setState em componentes desmontados.
  useEffect(() => {
    if (isBluebox) return undefined;
    let mounted = true;
    const recons = [];
    const acc = [];

    Promise.all([getIOTDocument(), onUpdateIOTDocument()]).then(
      ([iotQuery, updateIotSubscription]) => {
        if (!mounted) return;

        const refreshThing = async ({ thing }, index) => {
          try {
            const response = await API.graphql(
              graphqlOperation(iotQuery, { thingName: thing })
            );
            const iot =
              'getIOT' in response ? response.getIOT : response.data?.getIOT;

            if (!iot || !mounted) return;
            acc[index] = iot;
            setThingsData([...acc]);
          } catch (_) {
            // A subscription continua ativa e o proximo ciclo tenta novamente.
          }
        };

        things.forEach(async ({ thing }, index) => {
          try {
            await refreshThing({ thing }, index);
            if (!acc[index]) {
              notifyError('Não foi possível buscar as informações da Thing');
              return;
            }
            if (!mounted) return;

            // Subscription com reconnect: queda de rede / token expirado nao
            // deixa mais a tela com dados congelados sem aviso.
            const recon = createReconnectingSubscription({
              label: `config-aplicacao/${thing}`,
              buildSubscription: ({ next, error }) =>
                API.graphql(
                  graphqlOperation(updateIotSubscription, { thingName: thing })
                ).subscribe({ next, error }),
              onNext: ({ value }) => {
                const update = value && value.data && value.data.onUpdateIOT;
                if (!update || !mounted) return;
                acc[index] = update;
                setThingsData([...acc]);
              },
            });
            recons.push(recon);
          } catch (e) {
            // notifyError ja cobrira casos de falha de query
          }
        });
      }
    );

    return () => {
      mounted = false;
      recons.forEach((r) => {
        if (r && typeof r.unsubscribe === 'function') r.unsubscribe();
      });
    };
  }, [things, isBluebox]);

  useEffect(() => {
    if (selectedThing.id)
      // sessionCache: rendimento hidraulico e estatico por thingId —
      // navegar entre things e voltar nao re-fetcha.
      sessionCache('rendimentoHidraulico', selectedThing.id, () =>
        getRendimentoHidraulico(selectedThing.id)
      )
        .then(({ data }) => {
          setRendimentoHidraulico(data.value);
        })
        .catch(() => {
          // Endpoint /thing/efficiency/:id pode nao estar implementado no
          // backend pra todas as maquinas — silenciamos o erro pra nao
          // poluir notification + console. UI exibe '-' como fallback.
        });
  }, [selectedThing]);

  const getOperationalStatus = useCallback(
    (thingId) => {
      getThingOperationalStatus(thingId).then(({ data }) => {
        dispatch({
          type: 'APLICACAO/SET_OPERATIONAL_STATUS',
          data,
        });
      });
    },
    [dispatch]
  );

  // Polling de operational status a cada 60s, mas pausa quando a aba esta
  // oculta (visibilitychange). Em painel CCO 24/7 a aba pode ficar minimizada
  // por horas — polling contiua era desperdicio de rede + dados velhos
  // recentes na volta. Ao tornar visivel, dispara uma requisicao imediata.
  useEffect(() => {
    const thingId = things[activeIndex]?.id;
    if (!thingId) return undefined;

    let interval = null;

    const startPolling = () => {
      if (interval) return;
      getOperationalStatus(thingId);
      interval = setInterval(() => getOperationalStatus(thingId), 60000);
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) stopPolling();
      else startPolling();
    };

    if (!document.hidden) startPolling();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      stopPolling();
    };
    // `thingsData` removido das deps: estava re-iniciando o polling toda
    // vez que a subscription IoT atualizava (varias vezes por segundo),
    // gerando 4+ chamadas de operational-status em 2s. O polling so precisa
    // re-iniciar quando muda a thing selecionada (activeIndex).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [things, activeIndex, getOperationalStatus]);

  const hasOperationalStatus =
    Array.isArray(operationalStatus) && operationalStatus.length > 0;
  const currentMachine = things[activeIndex];

  return (
    <s.Container>
      <s.StatusCard empty={isBluebox ? false : !hasOperationalStatus}>
        {isBluebox ? (
          // Bluebox nao tem os bits Sta_* de bomba/UCHA — barra
          // equivalente derivada das grandezas MQTT dela.
          <BlueboxStatus data={thingsData[activeIndex]} />
        ) : (
          hasOperationalStatus && (
            <OperationalStatus operationalStatus={operationalStatus} />
          )
        )}
      </s.StatusCard>
      <s.ConfigCard>
        <s.HeaderBar>
          <s.HeaderLeft>
            <s.MachineCount>
              Máquina {activeIndex + 1} de {things?.length}
            </s.MachineCount>
            <s.MachineName>
              {currentMachine?.thing}
              {currentMachine?.model && <small>{currentMachine.model}</small>}
            </s.MachineName>
          </s.HeaderLeft>
          {zoomCard && (
            <Button
              background={colors.card}
              color={colors.lightblue}
              onClick={() => {
                history.push('/dashboard/customer/operational', {
                  thing: things[activeIndex],
                  aplicacao,
                  dataThingConfig: thingsData,
                });
              }}
              label="Operacional"
              width="140px"
              height="30px"
              hover
            />
          )}
        </s.HeaderBar>
        {!loading ? (
          zoomCard ? (
            <Carousel
              afterChange={afterChange}
              hideArrows={things.length <= 1}
              centerMode={false}
              responsive={{
                default: {
                  breakpoint: { max: 9999, min: 0 },
                  items: 1,
                  partialVisibilityGutter: 700,
                },
              }}
            >
              {things.map((n, index) => {
                const valuePressureDescargaOrRecalque = isBomba
                  ? thingsData[index] && thingsData[index]?.PressaoRecalque
                    ? `${formatValue(thingsData[index]?.PressaoRecalque)} mca`
                    : thingsData[index] &&
                      thingsData[index]?.PressaoRecalque === 0
                    ? '0 mca'
                    : '-'
                  : thingsData[index] && thingsData[index]?.PressaoDescarga
                  ? `${formatValue(thingsData[index]?.PressaoDescarga)} mca`
                  : thingsData[index] &&
                    thingsData[index]?.PressaoDescarga === 0
                  ? '0 mca'
                  : '-';

                // Energia geral pode chegar a ordens de GWh em historicos
                // longos. formatEnergyAutoUnit escala automaticamente
                // (kWh -> MWh -> GWh -> TWh) pra evitar numeros enormes.
                const valueEnergiaGeradaOrConsumida =
                  type === 'ucha'
                    ? thingsData[index] &&
                      typeof thingsData[index]?.EnergiaAtivaReversa ===
                        'number' &&
                      typeof thingsData[index]?.EnergiaAtiva === 'number'
                      ? formatEnergyAutoUnit(
                          thingsData[index]?.EnergiaAtivaReversa -
                            thingsData[index]?.EnergiaAtiva,
                          'kWh'
                        )
                      : thingsData[index] &&
                        thingsData[index]?.EnergiaAtivaReversa === 0 &&
                        thingsData[index]?.EnergiaAtiva === 0
                      ? '0 kWh'
                      : '-'
                    : thingsData[index] &&
                      typeof thingsData[index]?.EnergiaConsumidaTotal ===
                        'number'
                    ? formatEnergyAutoUnit(
                        thingsData[index]?.EnergiaConsumidaTotal,
                        'kWh'
                      )
                    : '-';

                return (
                  <s.CardWrapperContainer key={n.id}>
                    <s.Cell
                      row
                      imagem
                      onClick={() => {
                        history.push('/dashboard/customer/operational', {
                          thing: things[activeIndex],
                          aplicacao,
                          dataThingConfig: thingsData,
                        });
                      }}
                    >
                      {/* Setas de fluxo: escondidas na bluebox (o
                              foco e a inclinacao/nivel). UCHA/bomba mantem. */}
                      {!isBluebox && <s.Arrow src={icons.Arrow} alt="" />}
                      {(() => {
                        // BLUEBOX: a imagem inclina conforme a variavel
                        // Tilt (Inclinacao, em graus) — representa a
                        // inclinacao fisica do equipamento em tempo real.
                        // O valor exato aparece no tooltip. So bluebox.
                        // No simulador, cicla os angulos de teste
                        // (45/90/...) a cada ativacao pra ver cada
                        // posicao; senao usa o Tilt real da thing.
                        const tilt = simulatorEnabled
                          ? simulatedTilt(simulatorCycle + index)
                          : Number(thingsData[index] && thingsData[index].Tilt);
                        const hasTilt = isBluebox && Number.isFinite(tilt);
                        // Altura do palco proporcional ao grau: bounding
                        // box da imagem rotacionada. aspecto ~ altura/
                        // largura da imagem (IMG_ASPECT). Ratio =
                        // |sin| + aspecto*|cos| -> quase plano em graus
                        // pequenos, mais alto em 45/90, sem sobra.
                        const IMG_ASPECT = 0.34;
                        const rot = hasTilt ? tilt : 0;
                        const rad = (Math.abs(rot) * Math.PI) / 180;
                        const stageRatio =
                          Math.abs(Math.sin(rad)) +
                          IMG_ASPECT * Math.abs(Math.cos(rad));
                        const imgEl = (
                          <s.Image
                            src={machineImage}
                            flip={type === 'ucha'}
                            alt=""
                            title={
                              isBluebox
                                ? `Inclinação: ${
                                    hasTilt
                                      ? `${tilt.toLocaleString('pt-BR', {
                                          maximumFractionDigits: 1,
                                        })}°`
                                      : 'sem dado'
                                  }`
                                : undefined
                            }
                            style={
                              hasTilt
                                ? {
                                    transform: `rotate(${tilt}deg)`,
                                    transition: 'transform 400ms ease',
                                  }
                                : undefined
                            }
                          />
                        );
                        // Bluebox: envolve no palco com a linha de nivel
                        // fixa no centro pra evidenciar a inclinacao.
                        return isBluebox ? (
                          <s.TiltStage
                            style={{
                              aspectRatio: `1 / ${stageRatio.toFixed(3)}`,
                            }}
                          >
                            {imgEl}
                            <s.TiltLevelLine />
                            <s.TiltBadge>
                              {hasTilt
                                ? `${tilt.toLocaleString('pt-BR', {
                                    maximumFractionDigits: 1,
                                  })}°`
                                : '—'}
                            </s.TiltBadge>
                          </s.TiltStage>
                        ) : (
                          imgEl
                        );
                      })()}

                      {!isBluebox && <s.Arrow src={icons.Arrow} alt="" />}
                    </s.Cell>
                    {isBluebox &&
                      (() => {
                        // Bateria estilo mobile/Tesla abaixo da imagem
                        // (variavel Batt, %). Cor por nivel: verde ok,
                        // laranja baixo, vermelho critico.
                        const batt = Number(
                          thingsData[index] && thingsData[index].Batt
                        );
                        const hasBatt = Number.isFinite(batt);
                        const pct = hasBatt
                          ? Math.max(0, Math.min(100, batt))
                          : 0;
                        const color =
                          // eslint-disable-next-line no-nested-ternary
                          pct > 50
                            ? 'var(--cco-green)'
                            : pct > 20
                            ? 'var(--cco-orange)'
                            : 'var(--cco-red)';
                        return (
                          <s.BatteryWrap title="Estado da bateria">
                            <s.BatteryShell>
                              <s.BatteryFill $pct={pct} $color={color} />
                            </s.BatteryShell>
                            <s.BatteryLabel>
                              {hasBatt
                                ? `${pct.toLocaleString('pt-BR', {
                                    maximumFractionDigits: 0,
                                  })}%`
                                : '—'}
                            </s.BatteryLabel>
                          </s.BatteryWrap>
                        );
                      })()}
                    <s.CardWrapper>
                      {isBluebox ? (
                        // Cards de destaque da BLUEBOX (grandezas
                        // proprias). O resto vai no BlueboxDetail abaixo.
                        BLUEBOX_HEADLINE_CARDS.map((card) => (
                          <s.Cell key={card.key}>
                            <s.Text padding="5px 0">{card.label}</s.Text>
                            <s.ValueWrapper>
                              {formatBlueboxValue(
                                thingsData[index] &&
                                  thingsData[index][card.key],
                                card
                              )}
                            </s.ValueWrapper>
                          </s.Cell>
                        ))
                      ) : (
                        <>
                          <s.Cell>
                            <s.Text padding="5px 0">
                              {isBomba
                                ? 'Pressão Sucção'
                                : 'Pressão de Alimentação'}
                            </s.Text>
                            <s.ValueWrapper>
                              {thingsData[index] &&
                              thingsData[index]?.PressaoAlimentacao
                                ? `${formatValue(
                                    thingsData[index]?.PressaoAlimentacao
                                  )} mca`
                                : thingsData[index] &&
                                  thingsData[index]?.PressaoAlimentacao === 0
                                ? '0 mca'
                                : '-'}
                            </s.ValueWrapper>
                          </s.Cell>
                          {/*  */}
                          <s.Cell>
                            <s.Text padding="5px 0">Vazão </s.Text>
                            <s.ValueWrapper
                              clickable
                              onClick={() =>
                                setUnidadeMedida(
                                  unidadeMedida === 'litros'
                                    ? 'metros'
                                    : 'litros'
                                )
                              }
                            >
                              {thingsData[index] && thingsData[index]?.Vazao
                                ? `${
                                    unidadeMedida === 'litros'
                                      ? `${formatValue(
                                          thingsData[index]?.Vazao
                                        )} l/s`
                                      : `${formatValue(
                                          thingsData[index]?.Vazao * 3.6
                                        )} m³/h`
                                  } `
                                : thingsData[index] &&
                                  thingsData[index]?.Vazao === 0
                                ? '0 m³/h'
                                : '-'}
                              <s.FlipIcon src={icons.Flip} alt="" />
                            </s.ValueWrapper>
                          </s.Cell>

                          <s.Cell>
                            <s.Text padding="5px 0">
                              {isBomba
                                ? 'Pressão de Recalque'
                                : 'Pressão de Descarga'}
                            </s.Text>
                            <s.ValueWrapper>
                              {valuePressureDescargaOrRecalque}
                            </s.ValueWrapper>
                          </s.Cell>

                          <s.Cell>
                            <s.Text padding="5px 0">
                              {type === 'ucha'
                                ? 'Potência ativa'
                                : 'Potência consumida'}
                            </s.Text>
                            <s.ValueWrapper>
                              {thingsData[index] &&
                              thingsData[index]?.PotenciaAtivaMedia
                                ? `${formatValue(
                                    thingsData[index]?.PotenciaAtivaMedia
                                  )} kW`
                                : thingsData[index] &&
                                  thingsData[index]?.PotenciaAtivaMedia === 0
                                ? '0 kW'
                                : '-'}
                            </s.ValueWrapper>
                          </s.Cell>

                          <s.Cell>
                            <s.Text padding="5px 0">
                              Total de energia{' '}
                              {type === 'ucha' ? 'gerada' : 'consumida'}
                            </s.Text>
                            <s.ValueWrapper>
                              {valueEnergiaGeradaOrConsumida}
                            </s.ValueWrapper>
                          </s.Cell>

                          <s.Cell>
                            <s.Text padding="5px 0">
                              {isBomba
                                ? 'Rendimento da Bomba'
                                : 'Rendimento de Turbina'}
                            </s.Text>
                            <s.ValueWrapper>
                              {rendimentoHidraulico
                                ? `${formatValue(rendimentoHidraulico)}%`
                                : rendimentoHidraulico === 0
                                ? '0%'
                                : '-'}
                            </s.ValueWrapper>
                          </s.Cell>
                        </>
                      )}
                    </s.CardWrapper>
                  </s.CardWrapperContainer>
                );
              })}
            </Carousel>
          ) : (
            <Carousel
              centerMode
              hideArrows={things.length <= 1}
              afterChange={afterChange}
              responsive={{
                default: {
                  breakpoint: { max: 9999, min: 0 },
                  items: 1,
                  partialVisibilityGutter: 700,
                },
              }}
            >
              {things.map((n, index) => (
                <s.ClosedCardWrapper active={index === activeIndex}>
                  <h2>{n.thing}</h2>
                  <h4>
                    {isBomba ? 'Pressão Sucção' : 'Pressão de Alimentação'}
                  </h4>
                  <span>
                    {thingsData[index] && thingsData[index]?.PressaoAlimentacao
                      ? `${formatValue(
                          thingsData[index]?.PressaoAlimentacao
                        )} mca`
                      : thingsData[index] &&
                        thingsData[index]?.PressaoAlimentacao === 0
                      ? '0 mca'
                      : '-'}
                  </span>
                  <img
                    onClick={() => {
                      history.push('/dashboard/customer/operational', {
                        thing: things[activeIndex],
                        aplicacao,
                        dataThingConfig: thingsData,
                      });
                    }}
                    src={icons.ConjuntoBomba}
                    alt=""
                  />
                  <h4>
                    {isBomba ? 'Pressão de Recalque' : 'Pressão de Descarga'}
                  </h4>
                  <span>
                    {thingsData[index] && thingsData[index]?.PressaoDescarga
                      ? `${formatValue(thingsData[index]?.PressaoDescarga)} mca`
                      : thingsData[index] &&
                        thingsData[index]?.PressaoDescarga === 0
                      ? '0 mca'
                      : '-'}
                  </span>
                  <div style={{ marginTop: 12 }}>
                    <Button
                      background="transparent"
                      color={colors.lightblue}
                      onClick={() =>
                        history.push('/dashboard/customer/operational', {
                          thing: things[activeIndex],
                          aplicacao,
                        })
                      }
                      label="Operacional"
                      width="160px"
                      height="35px"
                    />
                  </div>
                </s.ClosedCardWrapper>
              ))}
            </Carousel>
          )
        ) : (
          <div
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              margin: 'auto',
            }}
          >
            <Loader />
          </div>
        )}
      </s.ConfigCard>
    </s.Container>
  );
};

export default ConfigAplicacao;
